import { yupResolver } from '@hookform/resolvers/yup';
import { Grid } from '@mui/material';
import { Button, FormContainer } from '@openx/components/core';
import type { Shape } from '@openx/types';
import { FormProvider, type SubmitHandler, useForm } from 'react-hook-form';
import { type ObjectSchema, object } from 'yup';
import { getEnvironmentConfigs } from '../../../environmentConfigs';
import { useAuth } from '../components/AuthContext';
import { InstanceSelect } from '../components/InstanceSelect';
import { SsoLayout } from '../components/SsoLayout';
import type { InstanceListOption } from '../types';
import { getDefaultInstance } from '../utils/getDefaultInstance';
import { redirectToAuthorize } from '../utils/redirectToAuthorize';

type FormValues = {
  instance: InstanceListOption | null;
};

const validationSchema = object({
  instance: object<null, Shape<InstanceListOption>>().nullable().required('Please select an instance'),
}) as ObjectSchema<FormValues>;

type InstanceSelectFormProps = {
  instancesList: InstanceListOption[];
  onLogout: () => void;
};

export const InstanceSelectForm = ({ instancesList, onLogout }: InstanceSelectFormProps) => {
  const { unityOrigin } = getEnvironmentConfigs();
  const { currentUser } = useAuth();

  const formMethods = useForm<FormValues>({
    defaultValues: { instance: getDefaultInstance(instancesList, Boolean(unityOrigin)) },
    mode: 'onChange',
    resolver: yupResolver<FormValues>(validationSchema),
  });
  const { formState, handleSubmit } = formMethods;

  const onSubmit: SubmitHandler<FormValues> = ({ instance }) => {
    if (!instance) {
      return;
    }

    redirectToAuthorize({
      instanceUID: instance.id,
      redirectUri: `${instance.urls[0]}/response-oidc`,
      email: currentUser?.email,
    });
  };

  const handleDocsButtonClick = (docsUrl: string) => {
    window.location.href = docsUrl;
  };

  return (
    <SsoLayout>
      <FormProvider {...formMethods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <FormContainer fullPage>
            <InstanceSelect instancesList={instancesList} onButtonClick={handleDocsButtonClick} />
            <Grid container spacing={2} direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
              <Grid>
                <Button variant="text" color="primary" onClick={onLogout} data-test="logout">
                  Log out
                </Button>
              </Grid>
              <Grid>
                <Button
                  type="submit"
                  color="primary"
                  variant="contained"
                  disabled={!formState.isValid || formState.isSubmitting}
                  data-test="authorize-button"
                >
                  Continue
                </Button>
              </Grid>
            </Grid>
          </FormContainer>
        </form>
      </FormProvider>
    </SsoLayout>
  );
};
